'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation';
import { gql, useMutation } from '@apollo/client'
import { RecipeIngredient, useCreateNoteMutation, useDeleteRecipeMutation, useGetRecipeLazyQuery, useListingDetailQuery, useLogShareMutation, useRecipeNoteLazyQuery, useRecipeStepsLazyQuery, useUserProfileLazyQuery } from 'graphql/generated/graphql';
import { RecipeShareModal } from './RecipeShareModal'
import { RecipeRatePromptModal } from './RecipeRatePromptModal'
import { useAppSelector } from 'features/hooks';
import { RootState } from 'features/store';
import { isUserAvailable } from 'helpers/authUser';
import { Dialog, DialogBody, DialogHeader, Typography } from '@material-tailwind/react';
import { PencilLine, Plus, Trash } from '@phosphor-icons/react';
import { PageBackButton } from '@/components/ui/page-back-button'
import { Share, QrCode, X, Bookmark, ShoppingBag } from 'lucide-react';
import { AddToShoppingListModal } from '@/components/shopping-lists/AddToShoppingListModal'
import type { RecipePayload } from '@/lib/shoppingLists/types'
import { canUseShopping } from '@/lib/shoppingLists/access'
import { Button, Modal, Spinner } from 'flowbite-react'
import CustomButton from '@/components/ui/Button'
import { Form, Formik } from 'formik';
import Input from '@/components/Input';
import { toast } from 'react-toastify';
import RatingStar from '@/components/RatingStar';
import FractionDisplay from '@/components/FractionDisplay';
import Link from 'next/link';
import ReviewRatings from '@/components/ReviewRatings';
import Image from 'next/image';
import { PageContainer } from '@/components/ui/page-container'
import { RecipeMobileTabs } from './RecipeMobileTabs'
import { RecipeQuickStats } from './RecipeQuickStats'
import { RecipeRightRail } from './RecipeRightRail'
import { useBookmarkedRecipeIds } from '@/hook/useBookmarkedRecipeIds'
import { useRecipeRatingPrompt } from '@/hook/useRecipeRatingPrompt'
import DOMPurify from 'isomorphic-dompurify'

interface RecipePageProps {
  slug: string
}

const actionPillClass =
  'flex h-10 items-center justify-center gap-2 rounded-full border border-[#CBD7DD] bg-[#F7F7F7] px-4 text-base text-[#548828] hover:bg-gray-100 transition-colors'
const actionIconClass =
  'flex h-10 w-10 items-center justify-center rounded-full border border-[#CBD7DD] bg-[#F7F7F7] hover:bg-gray-100 transition-colors'

function formatRecipeIngredientQuantity(item: {
  whole_number?: number | null
  numerator?: number | null
  denominator?: number | null
}) {
  const whole = Number(item.whole_number) || 0
  const numerator = Number(item.numerator) || 0
  const denominator = Number(item.denominator) || 0
  if (whole && numerator && denominator) return `${whole} ${numerator}/${denominator}`
  if (numerator && denominator) return `${numerator}/${denominator}`
  if (whole) return String(whole)
  return ''
}

const RecipePage: React.FC<RecipePageProps> = ({ slug }) => {
  const router = useRouter()
  const user = useAppSelector((state: RootState) => state.culineerAuth.user)
  const [wakeLockEnabled, setWakeLockEnabled] = useState<boolean>(false);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [recipeNotes, setRecipeNotes] = useState<any>([])
  const [userDetail, setUserDetail] = useState<any>()
  const [recipe, setRecipe] = useState<any>()
  const [recipeSteps, setRecipeSteps] = useState<any>([])
  const [imageProps, setImageProps] = useState({
    loading: true,
    imageError: false
  })

  const [paddingTop, setPaddingTop] = useState('0')
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [showQR, setShowQR] = useState(false)
  const [qrDownloading, setQrDownloading] = useState(false)
  const apiBase = process.env.NEXT_PUBLIC_API_ENDPOINT?.replace(/\/query$/, '') ?? 'https://dev.culineerapi.com'

  const [getRecipe, {loading, error}] = useGetRecipeLazyQuery({fetchPolicy: 'no-cache'})
  const [addRecipeNote] = useCreateNoteMutation({fetchPolicy: 'no-cache'})
  const [logShare] = useLogShareMutation()
  const [trackEvent] = useMutation(gql`mutation TrackEvent($input: TrackEventInput!) { trackEvent(input: $input) }`)

  const [getRecipeNotes] = useRecipeNoteLazyQuery({fetchPolicy: 'no-cache'})
  const [getUserData] = useUserProfileLazyQuery({fetchPolicy: 'no-cache'})
  const [getRecipeSteps] = useRecipeStepsLazyQuery({fetchPolicy: 'no-cache'})

  const [delRecipe, {loading: deleteRecipeLoading}] = useDeleteRecipeMutation({fetchPolicy: 'no-cache'})
  const [shareOpen, setShareOpen] = useState(false)
  const [addToListOpen, setAddToListOpen] = useState(false)
  const { bookmarkedIds, toggleBookmark } = useBookmarkedRecipeIds()
  const isOwnRecipe = !!user?.user_id && user.user_id === recipe?.user_id
  const isBookmarked = recipe?.recipe_id ? bookmarkedIds.has(recipe.recipe_id) : false

  const { data: listing } = useListingDetailQuery({
    fetchPolicy: 'no-cache',
    variables: { listingId: userDetail?.default_listing_id },
  })

  // producer/market must use their listing photo, never profile_photo_resource
  const getPublisherAvatarSrc = () => {
    const userType = userDetail?.user_type?.toLowerCase()
    if (userType === 'producer') {
      return userDetail?.default_listing?.photo || listing?.listingDetail?.photo || '/assets/icons/tractor.svg'
    }
    if (userType === 'market') {
      return userDetail?.default_listing?.photo || listing?.listingDetail?.photo || '/assets/icons/Market.svg'
    }
    return userDetail?.profile_photo_resource || '/assets/avatar/user-default.png'
  }

  const {
    open: ratePromptOpen,
    markCookModeStarted,
    onCookModeEnded,
    skip: skipRatePrompt,
    rate: goRateRecipe,
    dismissWithoutSnooze: dismissRatePrompt,
  } = useRecipeRatingPrompt({
    recipeId: recipe?.recipe_id,
    enabled: !!user?.user_id && !!recipe?.recipe_id && !isOwnRecipe,
    recipeSnapshot: recipe?.slug
      ? {
          slug: recipe.slug,
          recipe_id: recipe.recipe_id,
          recipe_name: recipe.recipe_name,
          resourcePath: recipe.resourcePath,
          recipe_img_url: recipe.recipe_img_url,
          thumbnailPath: recipe.thumbnailPath,
          author_name: recipe.author_name,
          publisherName: userDetail?.full_name || userDetail?.username,
          publisherAvatar: getPublisherAvatarSrc(),
        }
      : null,
  })

  const cookSessionActiveRef = useRef(false)
  const getRecipeDetail = async () => {
    const res = (await getRecipe({
        variables: {
            slug,
        }
    })).data

    setRecipe(res?.recipe)
    setRecipeNotes(res?.recipe?.recipe_notes)

    if(res?.recipe?.recipe_id) {
      trackEvent({ variables: { input: {
        event_type: 'recipe_viewed',
        entity_type: 'recipe',
        entity_id: String(res.recipe.recipe_id),
      }}}).catch(() => {})
    }

    if(res?.recipe) {
        const userRes = (await getUserData({
            variables: {
                slug: res?.recipe.user?.slug
            }
        })).data
        setUserDetail(userRes?.userProfile)
    }
}


  useEffect(() => {
    getRecipeDetail()
    //     // getSteps()
    //     // getNotes()
  }, [slug])

  const toggleCookMode = useCallback(() => {
    setWakeLockEnabled((prev) => !prev)
  }, [])

  useEffect(() => {
    if (wakeLockEnabled) {
      cookSessionActiveRef.current = true
      markCookModeStarted()
      return
    }
    if (cookSessionActiveRef.current) {
      cookSessionActiveRef.current = false
      onCookModeEnded()
    }
  }, [wakeLockEnabled, markCookModeStarted, onCookModeEnded])

  useEffect(() => {
    if (!wakeLockEnabled) {
      wakeLockRef.current?.release().catch(() => {})
      wakeLockRef.current = null
      return
    }

    if (!('wakeLock' in navigator)) {
      toast.info('Cook mode is not supported in this browser.')
      setWakeLockEnabled(false)
      return
    }

    let cancelled = false

    navigator.wakeLock
      .request('screen')
      .then((sentinel) => {
        if (cancelled) {
          sentinel.release().catch(() => {})
          return
        }
        wakeLockRef.current = sentinel
        sentinel.onrelease = () => {
          wakeLockRef.current = null
        }
      })
      .catch((err) => {
        console.error('Error requesting screen wake lock:', err)
        if (!cancelled) {
          setWakeLockEnabled(false)
          toast.info('Could not keep your screen awake. Try tapping the page first.')
        }
      })

    return () => {
      cancelled = true
    }
  }, [wakeLockEnabled])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState !== 'visible' ||
        !wakeLockEnabled ||
        wakeLockRef.current ||
        !('wakeLock' in navigator)
      ) {
        return
      }

      navigator.wakeLock
        .request('screen')
        .then((sentinel) => {
          wakeLockRef.current = sentinel
          sentinel.onrelease = () => {
            wakeLockRef.current = null
          }
        })
        .catch(() => {})
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [wakeLockEnabled])

  const handleOnSubmit = async (values: {note_name: string}) => {
    try {
        const resNote = (await addRecipeNote({
            variables: {
                input: [{
                    recipe_id: recipe?.recipe_id,
                    note_name: values.note_name,
                    note_order: recipeNotes.length as number + 1,
                    user_created: user.user_id
                }]
            }
        })).data

      setRecipeNotes([...recipeNotes, ...resNote?.createNote])

      setOpenModal(false)
      toast.success('Note added successfully')
    } catch (error: any) {
      toast.error('Whoops! error occured')
      console.error('Error adding note:', error.message)
    }
  }

  const recipeModification = () => router.push(`/recipes/${recipe?.slug}/edit`)
  const initialRecipeDelete = () => setOpenDeleteModal(!openDeleteModal)

  const deleteRecipe = async () => {
    try {
      const delRes = await delRecipe({
        variables: {
          recipeId: recipe?.recipe_id
        }
      })

      if(delRes.data?.deleteRecipe) {
        setOpenDeleteModal(!openDeleteModal)
        router.push('/recipes')
      }

    } catch (error: any) {
      setOpenDeleteModal(!openDeleteModal)
      toast.error(error?.message)
    }
  }

  const checkUserType = () => {
    const userType = userDetail?.user_type?.toLowerCase()
    let url = ''
    if (userType === 'market') {
      url = `/market/${userDetail?.default_listing?.slug || listing?.listingDetail?.slug}`
    } else if (userType === 'producer') {
      url = `/producer/${userDetail?.default_listing?.slug || listing?.listingDetail?.slug}`
    } else {
      url = `/member/${userDetail?.slug}`
    }

    return url
  }

  // producer/market must use their listing photo, never profile_photo_resource
  // (see docs/avatar-resolution-scope.md) — mirrors checkUserType()'s branching above.
  const openShareModal = () => setShareOpen(true)

    if(loading) {
        return (
          <div className='flex items-center justify-center h-screen'>
              <Spinner aria-label="loading" size="xl" color={'success'} />
          </div>
        )
    }

    if(error) {
        return (
            <div className='flex flex-col items-center justify-center h-screen'>
                <div className='text-xl font-bold text-darkGray'>{error.message}</div>
                <div className='text-lg text-primary'>
                    <Link href={'/recipes'}>Go back to recipes</Link>
                </div>
            </div>
        )
    }

  const sortedIngredients = [...(recipe?.recipe_ingredients || [])]
    .map((recipeIngredient: RecipeIngredient) => ({
      record_id: recipeIngredient?.record_id,
      sort_order: recipeIngredient?.sort_order,
      ingredient_id: recipeIngredient.ingredient_id,
      whole_number: recipeIngredient?.whole_number,
      numerator: recipeIngredient?.numerator,
      denominator: recipeIngredient?.denominator,
      unit_abbreviation: recipeIngredient?.unit_of_measurement?.unit_abbreviation,
      name: recipeIngredient?.ingredient?.ingredient_name,
    }))
    .sort((a: any, b: any) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))

  const sortedSteps = [...(recipe?.recipe_steps || [])].sort(
    (a: any, b: any) => (a?.step_order ?? 0) - (b?.step_order ?? 0)
  )

  const shoppingRecipe: RecipePayload | null = recipe?.recipe_id
    ? {
        id: String(recipe.recipe_id),
        name: recipe.recipe_name || 'Recipe',
        slug: recipe.slug,
        ingredients: sortedIngredients
          .filter((item: { name?: string | null }) => Boolean(item.name))
          .map((item: {
            name?: string | null
            whole_number?: number | null
            numerator?: number | null
            denominator?: number | null
            unit_abbreviation?: string | null
          }) => ({
            name: item.name as string,
            quantity: formatRecipeIngredientQuantity(item),
            unit: item.unit_abbreviation || '',
          })),
      }
    : null

  const plannerRecipe =
    isUserAvailable(user) && shoppingRecipe
      ? {
          id: shoppingRecipe.id,
          name: shoppingRecipe.name,
          slug: shoppingRecipe.slug,
          image:
            recipe?.resourcePath ||
            recipe?.recipe_img_url ||
            recipe?.recipe_image?.resource_path ||
            '',
          servings: Number(recipe?.number_of_servings) || undefined,
          cookTime: Number(recipe?.cook_time ?? recipe?.total_time) || 0,
          prepTime: Number(recipe?.preparation_time) || 0,
          ingredients: shoppingRecipe.ingredients,
        }
      : null

  return (
    <div className='w-full h-full overflow-y-auto'>
      {wakeLockEnabled && (
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 bg-[#CCE9AD] px-4 py-2.5 text-sm font-semibold text-[#548828] xl:hidden">
          <span>Screen stays on · Cook Mode</span>
          <button
            type="button"
            onClick={toggleCookMode}
            className="rounded-full bg-white/70 px-3 py-1 text-sm font-semibold text-[#548828]"
          >
            Turn off
          </button>
        </div>
      )}

      <PageContainer className="py-6 sm:py-8">
        <div className="flex w-full max-w-full flex-col gap-8 xl:flex-row xl:items-start xl:gap-8">
          <div className="flex min-w-0 w-full flex-1 flex-col gap-6">
            <div className="flex items-center justify-between gap-3">
              <PageBackButton fallbackHref="/recipes" />
              <div className="flex shrink-0 items-center gap-3">
                {isUserAvailable(user) && canUseShopping(user) && shoppingRecipe && (
                  <button
                    type="button"
                    onClick={() => setAddToListOpen(true)}
                    aria-label="Add in Shopping List"
                    className="flex h-10 items-center justify-center gap-2 rounded-full bg-[#85C349] px-4 text-base font-semibold tracking-[-0.01em] text-white transition-colors hover:bg-[#76B33E]"
                  >
                    <ShoppingBag size={20} color="#FFFFFF" />
                    <span className="hidden sm:inline">Add in Shopping List</span>
                  </button>
                )}
                <button type="button" onClick={openShareModal} className={actionPillClass}>
                  <Share size={20} color="#2E7D0F" />
                  <span className="hidden sm:inline">Share</span>
                </button>
                {recipe?.slug && (
                  <button
                    type="button"
                    onClick={() => setShowQR(true)}
                    title="Get QR code"
                    className={actionIconClass}
                  >
                    <QrCode size={20} color="#548828" />
                  </button>
                )}
                {!isOwnRecipe && recipe?.recipe_id && (
                  <button
                    type="button"
                    onClick={() => toggleBookmark(recipe.recipe_id)}
                    title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
                    className={actionIconClass}
                  >
                    <Bookmark
                      size={20}
                      color={isBookmarked ? '#4A90E2' : '#548828'}
                      className={isBookmarked ? 'fill-[#4A90E2]' : ''}
                    />
                  </button>
                )}
                {user?.user_id === recipe?.user_id && (
                  <>
                    <button
                      type="button"
                      title="Edit"
                      onClick={recipeModification}
                      className={actionIconClass}
                    >
                      <PencilLine size={20} color="#548828" />
                    </button>
                    <button
                      type="button"
                      title="Delete"
                      onClick={initialRecipeDelete}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-red-300 bg-[#F7F7F7] hover:bg-red-50 transition-colors"
                    >
                      <Trash size={20} color="#ef4444" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {showQR && recipe?.slug && (() => {
              const recipeName = recipe?.recipe_name || 'Recipe'
              const qrSrc = `${apiBase}/og/qr/recipe/${recipe.slug}.png`
              const filename = recipeName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '-qr.png'
              const downloadQr = async () => {
                setQrDownloading(true)
                try {
                  const res = await fetch(qrSrc)
                  if (!res.ok) throw new Error('Failed to fetch QR code')
                  const blob = await res.blob()
                  const objectUrl = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = objectUrl
                  a.download = filename
                  document.body.appendChild(a)
                  a.click()
                  document.body.removeChild(a)
                  URL.revokeObjectURL(objectUrl)
                  if (user?.slug) {
                    logShare({ variables: { input: { content_type: 'recipe', content_id: String(recipe.recipe_id), share_channel: 'qr', ref_slug: user.slug } } }).catch(() => {})
                  }
                } catch {
                  toast.error('Failed to download QR code')
                } finally {
                  setQrDownloading(false)
                }
              }
              return (
                <div
                  className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'
                  onClick={() => setShowQR(false)}
                >
                  <div
                    className='bg-white rounded-2xl p-6 flex flex-col items-center gap-4 max-w-xs w-full mx-4 shadow-xl'
                    onClick={e => e.stopPropagation()}
                  >
                    <div className='flex items-center justify-between w-full'>
                      <h3 className='text-base font-semibold text-[#222]'>{recipeName} QR Code</h3>
                      <button onClick={() => setShowQR(false)} className='text-gray-400 hover:text-gray-600'>
                        <X size={20} />
                      </button>
                    </div>
                    <img
                      src={qrSrc}
                      alt={`QR code for ${recipeName}`}
                      className='w-48 h-48 rounded-lg'
                    />
                    <p className='text-sm text-gray-500 text-center'>
                      Anyone who scans this goes straight to the recipe.
                    </p>
                    <button
                      onClick={downloadQr}
                      disabled={qrDownloading}
                      className='w-full text-center py-2.5 bg-primary text-white rounded-full text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-70'
                    >
                      {qrDownloading ? 'Downloading…' : 'Download PNG'}
                    </button>
                  </div>
                </div>
              )
            })()}

            <div className="flex flex-col gap-2.5">
              <h1 className="text-[32px] font-semibold leading-10 text-[#202020] sm:text-[40px] sm:leading-[48px]">
                {recipe?.recipe_name}
              </h1>
              <div className="flex items-center gap-1">
                <RatingStar rating={Number(recipe?.average_recipe_rating)} />
              </div>
            </div>

            <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-2">
                <span className="text-[13px] italic leading-4 text-[#6B6B6B]">
                  Published by
                </span>
                <Link href={`${checkUserType()}`} className="flex items-center gap-2">
                  <img
                    src={getPublisherAvatarSrc()}
                    alt={userDetail?.full_name?.replace(' ', '-') || 'Publisher'}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                  {userDetail?.full_name && (
                    <span className="text-base font-medium leading-[19px] text-[#202020]">
                      {userDetail.full_name}
                    </span>
                  )}
                </Link>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
                {recipe?.author_name && (
                  <div className="flex flex-col">
                    <span className="text-base leading-7 text-[#0D1000]">
                      {recipe.author_name}
                    </span>
                    <span className="text-sm leading-7 text-[#6B6B6B]">Author</span>
                  </div>
                )}
                {recipe?.author_name && recipe?.inspired_by && (
                  <div className="hidden h-[50px] w-px bg-[#D8D8D8] sm:block" />
                )}
                {recipe?.inspired_by && (
                  <div className="flex min-w-0 flex-col">
                    <Link
                      href={`${recipe.inspired_by}`}
                      className="truncate text-base leading-7 text-[#3063C5] underline"
                    >
                      {recipe.inspired_by}
                    </Link>
                    <span className="text-sm leading-7 text-[#6B6B6B]">Inspired by</span>
                  </div>
                )}
              </div>
            </div>

            <div className="relative mx-auto aspect-[16/9] w-full max-w-[1200px] max-h-[394px] overflow-hidden rounded-[20px] bg-[#F7F7F7]">
              {recipe?.resourcePath || recipe?.recipe_img_url ? (
                <Image
                  src={
                    imageProps.imageError
                      ? '/assets/recipe-filled-grey.svg'
                      : ((recipe?.resourcePath || recipe?.recipe_img_url) as string)
                  }
                  alt={recipe?.recipe_name || 'Recipe Image'}
                  fill
                  sizes="(max-width: 1440px) calc(100vw - 64px), 1200px"
                  className="object-cover"
                  onLoad={() =>
                    setImageProps((prev) => ({ ...prev, loading: false }))
                  }
                  onError={() =>
                    setImageProps((prev) => ({
                      ...prev,
                      loading: false,
                      imageError: true,
                    }))
                  }
                />
              ) : (
                <img
                  className="mx-auto h-48 w-auto sm:h-64"
                  src={'/assets/recipe-filled-grey.svg'}
                  alt=""
                />
              )}
            </div>

            <RecipeQuickStats
              recipe={recipe}
              wakeLockEnabled={wakeLockEnabled}
              onToggle={toggleCookMode}
            />

            <div className="sm:hidden">
              <RecipeMobileTabs
                recipe={recipe}
                recipeNotes={recipeNotes}
                userId={user?.user_id}
                onAddNote={() => setOpenModal(!openModal)}
              />
            </div>

            <div className="hidden gap-8 sm:flex sm:flex-row sm:items-start">
              <div className="w-[42%] shrink-0">
                <h3 className="mb-5 text-xl font-semibold leading-7 text-[#202020]">
                  Ingredients
                </h3>
                {sortedIngredients.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    {sortedIngredients.map((item, index: number) => (
                      <div
                        key={item.record_id ?? `${item.ingredient_id}-${index}`}
                        className="flex items-start gap-3 text-sm leading-[17px] text-[#0D1000]"
                      >
                        <span className="min-w-[28px] shrink-0 font-semibold">
                          <FractionDisplay
                            whole_nmuber={Number(item?.whole_number)}
                            numerator={Number(item?.numerator)}
                            denominator={Number(item?.denominator)}
                          />
                        </span>
                        <span className="w-[60px] shrink-0">
                          {item?.unit_abbreviation ?? ''}
                        </span>
                        <span
                          className="min-w-0 flex-1"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(item?.name ?? ''),
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#6B6B6B]">Not available</p>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <h3 className="mb-5 text-xl font-semibold leading-7 text-[#202020]">
                  Directions
                </h3>
                <div className="flex flex-col gap-5">
                  {sortedSteps.length > 0 ? (
                    sortedSteps.map((step: any, index: number) => (
                      <div key={index} className="flex flex-col gap-2">
                        <h4 className="text-base font-bold leading-[22px] text-[#202020]">
                          Step {index + 1}
                        </h4>
                        <div
                          className="text-base font-normal leading-6 text-[#333333]"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(step?.step_name as string ?? ''),
                          }}
                        />
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-[#6B6B6B]">No directions available</p>
                  )}
                </div>

                <div className="mt-10">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-xl font-semibold leading-6 text-[#202020]">Notes</h3>
                      <p className="mt-1 text-sm leading-7 text-[#333333]">
                        Keep in mind these things. These are provided by recipe creator.
                      </p>
                    </div>
                    {user?.user_id === recipe?.user_id && (
                      <button
                        type="button"
                        onClick={() => setOpenModal(!openModal)}
                        className="flex shrink-0 items-center gap-1 rounded-full border border-[#85C349] px-2.5 py-1"
                      >
                        <Plus size={13.5} color="#85C349" />
                        <span className="text-[13px] text-[#85C349]">Add note</span>
                      </button>
                    )}
                  </div>
                  {Number(recipeNotes?.length) > 0 && (
                    <div className="flex flex-col gap-3 text-base leading-6 text-[#333333]">
                      {[...recipeNotes]
                        .sort((a: any, b: any) => a?.note_order - b?.note_order)
                        .map((note: any, index: number) => (
                          <div
                            key={index}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(note?.note_name as string ?? ''),
                            }}
                          />
                        ))}
                    </div>
                  )}
                </div>

                {recipe?.categories && recipe?.categories?.length > 0 && (
                  <div className="mt-8">
                    <h3 className="mb-3 text-xl font-semibold leading-6 text-[#202020]">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {recipe.categories?.map((tag: any, index: number) => (
                        <span
                          key={index}
                          className="inline-flex h-8 items-center rounded-full border border-[#CBD7DD] px-2.5 text-sm leading-7 text-[#202020]"
                        >
                          {tag?.category_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div id="recipe-reviews" className="scroll-mt-24 border-t border-[#D8D8D8] pt-6">
              <ReviewRatings
                recipeId={recipe?.recipe_id}
                userId={user?.user_id}
                user={user}
                averageRating={Number(recipe?.average_recipe_rating) || 0}
                recipeOwnerId={recipe?.user_id}
                totalRatings={Number(recipe?.recipe_reviews?.length) || 0}
                ratingsBreakdown={
                  recipe?.recipe_reviews?.map((review: any) => review?.rating) ||
                  []
                }
              />
            </div>
          </div>

          <RecipeRightRail plannerRecipe={plannerRecipe} />
        </div>
      </PageContainer>
      {openModal && (
        <Modal
          show={openModal}
          onClose={() => setOpenModal(false)}
          className="border-none bg-blue-gray-200 shadow-sm"
        >
          <Modal.Header className="border-none">Add Recipe Note</Modal.Header>
          <Modal.Body>
            <div className="">
              <Formik
                initialValues={{ note_name: '', note_order: 0 }}
                onSubmit={(values) => handleOnSubmit(values)}
              >
                {({ values, handleChange, handleBlur, handleSubmit }) => (
                  <Form onSubmit={handleSubmit}>
                    <div className="mb-10">
                      <Input
                        label="Note name"
                        name="note_name"
                        value={values.note_name}
                        placeholder="Note"
                        className="border-gray h-12 rounded-xl border focus-within:border-primary"
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className="mb-10">
                      <Input
                        type="numeric"
                        label="Note order"
                        value={Number(recipeNotes.length) + 1}
                        readOnly={true}
                        placeholder="Note order"
                        className="border-gray mt-4 h-12 rounded-xl border focus-within:border-primary"
                      />
                    </div>
                    <Modal.Footer className="border-none">
                      <Button color="gray" onClick={() => setOpenModal(false)} className="bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300">
                        Cancel
                      </Button>
                      <Button type="submit" color="green" className="px-5">
                        Add
                      </Button>
                    </Modal.Footer>
                  </Form>
                )}
              </Formik>
            </div>
          </Modal.Body>
        </Modal>
      )}
      {openDeleteModal && (
        <Dialog
          open={openDeleteModal}
          handler={() => setOpenDeleteModal(!openDeleteModal)}
          dismiss={{ enabled: false }}
          size={'xs'}
        >
          <DialogHeader className={'border-none'}>
            <Typography variant="h5" color="blue-gray">
              Notice
            </Typography>
          </DialogHeader>
          <DialogBody divider className="grid place-items-center gap-4">
            <Typography className="text-center text-xl font-normal">
              {`Are you sure to delete this recipe?`}
            </Typography>
            <div className="flex w-full place-content-end space-x-2 pt-8">
              <CustomButton
                className="flex w-24 items-center justify-center border"
                ghost
                onClick={() => {
                  setOpenDeleteModal(!openDeleteModal)
                }}
              >
                No
              </CustomButton>
              <CustomButton
                className="flex w-24 items-center justify-center"
                onClick={deleteRecipe}
              >
                Yes
              </CustomButton>
            </div>
          </DialogBody>
        </Dialog>
      )}

      {isUserAvailable(user) && canUseShopping(user) ? (
        <AddToShoppingListModal
          open={addToListOpen}
          onClose={() => setAddToListOpen(false)}
          recipe={shoppingRecipe}
        />
      ) : null}

      {recipe && (
        <RecipeShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          recipe={recipe}
          userSlug={
            userDetail?.user_type === 'producer' || userDetail?.user_type === 'market'
              ? listing?.listingDetail?.slug || userDetail?.default_listing?.slug
              : user?.slug
          }
        />
      )}

      {recipe?.slug && (
        <RecipeRatePromptModal
          open={ratePromptOpen}
          onSkip={skipRatePrompt}
          onRate={goRateRecipe}
          onDismissWithoutSnooze={dismissRatePrompt}
          recipe={{
            slug: recipe.slug,
            recipe_id: recipe.recipe_id,
            recipe_name: recipe.recipe_name,
            resourcePath: recipe.resourcePath,
            recipe_img_url: recipe.recipe_img_url,
            thumbnailPath: recipe.thumbnailPath,
            author_name: recipe.author_name,
            publisherName: userDetail?.full_name || userDetail?.username,
            publisherAvatar: getPublisherAvatarSrc(),
          }}
        />
      )}
    </div>
  )
}

export default RecipePage
